/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useLayoutEffect, useState} from 'react';
import debounce from 'lodash.debounce';
import isUndefined from 'lodash.isundefined';

/**
 * Defines the prop definitions for additional options ...
 *
 * elementId: The Id of the element.
 * property:  CSS Property of the element.
 * value:     JS Property which fetches the CSS Propery
 *            value.
 */
export type AdditionalOptions = {
  elementId: string;
  property: string;
  value: string;
};

/**
 * Provides a utility hook to get current size of the browser window.
 * This hook returns an object containing the window's width and height.
 *
 *
 * ```typescript
 * import {useWindowSize} from '@ion/dls/src/hooks/';
 *
 * const {width, height} = useWindowSize();
 * ```
 */
export function useWindowSize(
  debounceDelay = 300,
  additionalOptions?: AdditionalOptions,
  callback?: Function
) {
  const dimensions = {
    width  : undefined,
    height : undefined
  };
  const hasAdditionalOptions = !isUndefined(additionalOptions);
  if (hasAdditionalOptions) {
    dimensions[additionalOptions?.property] = undefined;
  }

  const [windowSize, setWindowSize] = useState(dimensions);
  useLayoutEffect(() => {
    function handleResize() {
      dimensions.width = window.innerWidth;
      dimensions.height = window.innerHeight;
      if (hasAdditionalOptions) {
        const {elementId, property, value} = additionalOptions;
        dimensions[property] = document.getElementById(elementId)?.[value];
      }
      setWindowSize(dimensions);
    }
    const func = callback || handleResize;
    window.addEventListener('resize', debounce(func, debounceDelay));
    func();
    return () => window.removeEventListener('resize', func as any);
  }, [callback]);
  return windowSize;
}

export default useWindowSize;
